import type { EmailEditorContent, EmailEditorDesign } from '@/types';

const EMAIL_EDITOR_DESIGN_ATTR = 'data-email-editor-design';
const EMBEDDED_DESIGN_PATTERN = /<div[^>]*data-email-editor-design="([^"]*)"[^>]*><\/div>/gi;

export const parseContentDesign = (contentDesign: unknown): EmailEditorDesign | null => {
  if (!contentDesign) {
    return null;
  }

  if (typeof contentDesign === 'string') {
    try {
      const parsed = JSON.parse(contentDesign);
      if (!parsed || typeof parsed !== 'object') {
        return null;
      }
      return parsed as EmailEditorDesign;
    } catch {
      return null;
    }
  }

  if (typeof contentDesign === 'object') {
    return JSON.parse(JSON.stringify(contentDesign)) as EmailEditorDesign;
  }

  return null;
};

const checkDesignContents = (contents: EmailEditorContent[]): boolean => {
  if (!Array.isArray(contents) || contents.length === 0) {
    return false;
  }

  return contents.some((content) => {
    if (!content || typeof content !== 'object') {
      return false;
    }

    if (Array.isArray(content.columns)) {
      return content.columns.some(
        (column) => column && Array.isArray(column.contents) && checkDesignContents(column.contents)
      );
    }

    return true;
  });
};

export const hasEmailEditorDesignContent = (design: EmailEditorDesign | null | undefined): boolean => {
  if (!design?.body?.rows) {
    return false;
  }

  return design.body.rows.some(
    (row) =>
      Array.isArray(row.columns) &&
      row.columns.some((column) => column && Array.isArray(column.contents) && checkDesignContents(column.contents))
  );
};

export const stripEmbeddedEmailEditorDesign = ({ html }: { html: string }): string => {
  if (!html) {
    return html;
  }

  return html.replace(EMBEDDED_DESIGN_PATTERN, '');
};

export const extractEmailEditorDesign = ({
  html,
}: {
  html: string;
}): { html: string; design: EmailEditorDesign | null } => {
  if (!html) {
    return { html: html ?? '', design: null };
  }

  const match = html.match(/<div[^>]*data-email-editor-design="([^"]*)"[^>]*><\/div>/i);
  if (!match?.[1]) {
    return { html, design: null };
  }

  try {
    return {
      html: html.replace(match[0], ''),
      design: parseContentDesign(JSON.parse(decodeURIComponent(match[1]))),
    };
  } catch {
    return {
      html: html.replace(match[0], ''),
      design: null,
    };
  }
};

export const embedEmailEditorDesign = ({
  html,
  design,
}: {
  html: string;
  design: EmailEditorDesign | null | undefined;
}): string => {
  const htmlWithoutDesign = stripEmbeddedEmailEditorDesign({ html });
  if (!hasEmailEditorDesignContent(design)) {
    return htmlWithoutDesign;
  }

  return `${htmlWithoutDesign}<div hidden ${EMAIL_EDITOR_DESIGN_ATTR}="${encodeURIComponent(JSON.stringify(design))}"></div>`;
};
